/**
 * Product Comparison Tracker
 *
 * Detects when a shopper is comparing products by tracking navigation
 * between product pages within a time window.
 *
 * Fires when: 2+ products are each viewed 3+ times within 2 minutes
 */

import {
  SHOPPER_INTENT_EVENTS,
  type ProductComparisonEventData,
} from "@redotech/redo-storefront-widget-common/shopper-intent";
import { fireIntentEvent, subscribeToNavigation } from "./types";

interface ProductView {
  timestamp: number;
  productId: string;
}

const STORAGE_KEY = "redo-product-comparison-state";
const PRODUCT_COMPARISON_THRESHOLD = 3;
const PRODUCT_COMPARISON_WINDOW_MS = 120_000;

interface ProductComparisonState {
  productViews: ProductView[];
  hasFired: boolean;
}

function loadState(): ProductComparisonState {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore storage errors
  }
  return { productViews: [], hasFired: false };
}

function saveState(state: ProductComparisonState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors
  }
}

function pruneOldEntries(entries: ProductView[]): ProductView[] {
  const cutoff = Date.now() - PRODUCT_COMPARISON_WINDOW_MS;
  return entries.filter((e) => e.timestamp > cutoff);
}

function detectCurrentProductId(): string | null {
  const shopifyProduct = (globalThis as any).ShopifyAnalytics?.meta?.product;
  if (shopifyProduct?.id) {
    return String(shopifyProduct.id);
  }

  const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
  if (match) {
    return match[1] ?? null;
  }

  return null;
}

export interface ProductComparisonTracker {
  cleanup: () => void;
}

export function setupProductComparisonTracker(): ProductComparisonTracker {
  const state = loadState();

  // Skip setup entirely if already fired
  if (state.hasFired) {
    return { cleanup: () => {} };
  }

  state.productViews = pruneOldEntries(state.productViews);
  saveState(state);

  let currentProductId = detectCurrentProductId();

  const checkAndFireEvent = (): void => {
    if (state.hasFired) {
      return;
    }

    const recentViews = pruneOldEntries(state.productViews);

    if (recentViews.length < PRODUCT_COMPARISON_THRESHOLD * 2) {
      return;
    }

    // Count views per product
    const productCounts = new Map<string, number>();
    for (const view of recentViews) {
      productCounts.set(
        view.productId,
        (productCounts.get(view.productId) ?? 0) + 1,
      );
    }

    // Find products viewed multiple times
    const comparedProducts: string[] = [];
    for (const [productId, count] of productCounts) {
      if (count >= PRODUCT_COMPARISON_THRESHOLD) {
        comparedProducts.push(productId);
      }
    }

    // If 2+ products are being compared
    if (comparedProducts.length >= 2) {
      state.hasFired = true;
      saveState(state);

      const eventData: ProductComparisonEventData = {
        products: comparedProducts,
        viewCounts: Object.fromEntries(productCounts),
        windowMs: PRODUCT_COMPARISON_WINDOW_MS,
      };

      fireIntentEvent({
        type: SHOPPER_INTENT_EVENTS.PRODUCT_COMPARISON,
        data: eventData,
      });
    }
  };

  const recordProductView = (productId: string): void => {
    state.productViews.push({ timestamp: Date.now(), productId });

    state.productViews = pruneOldEntries(state.productViews);
    saveState(state);
    checkAndFireEvent();
  };

  // Record initial product view if on a product page
  if (currentProductId) {
    recordProductView(currentProductId);
  }

  const handleNavigation = (): void => {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (match) {
      const productId = match[1];
      if (productId && productId !== currentProductId) {
        currentProductId = productId;
        recordProductView(productId);
      }
    }
  };

  // Subscribe to shared navigation listener
  const unsubscribeNavigation = subscribeToNavigation(handleNavigation);

  return {
    cleanup: () => {
      unsubscribeNavigation();
    },
  };
}
